/**
 * TagManagementSection - Manages KPIs and Locations for an organization
 *
 * Extracted from AdminOrganizationDashboard for reuse in OrganizationManagement screen.
 * Provides CRUD operations for organization configuration tags.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';

import CustomText from '../CustomText';
import ChipSection from '../ChipSection';
import QRCodeModal from '../QRCodeModal';
import { Chip_Styles, Web_Chip_Styles } from '../../style/ChipStyles';
import { useResponsiveStyles } from '../../hooks';
import Util from '../../util/util';
import AppStyles from '../../AppStyles';
import { useTranslation } from '../../i18n';

/**
 * @param {string} organization - Current organization
 * @param {string} selectedOrg - Selected organization from picker (may differ from current)
 */
const TagManagementSection = ({ organization, selectedOrg }) => {
  const { t } = useTranslation(['admin', 'common']);

  const { isDesktop } = useResponsiveStyles();
  const effectiveOrg = selectedOrg || organization;

  const [KPIChips, setKPIChips] = useState([]);
  const [LocationChips, setLocationChips] = useState([]);
  const [ShouldUpdateKPIs, setShouldUpdateKPIS] = useState(false);
  const [ShouldUpdateLocations, setShouldUpdateLocations] = useState(false);
  const [loading, setLoading] = useState(true);
  const [QRModalLocation, setQRModalLocation] = useState(null);

  const maxDelete = 1;

  // Fetch chips when organization changes
  useEffect(() => {
    setLoading(true);
    Promise.all([getChips('KPI'), getChips('Location')])
      .then(([kpis, locations]) => {
        setKPIChips(Util.textOnly(kpis).sort());
        setLocationChips(Util.textOnly(locations).sort());
      })
      .finally(() => setLoading(false));
  }, [effectiveOrg]);

  // Handle KPI updates
  useEffect(() => {
    if (ShouldUpdateKPIs) {
      setShouldUpdateKPIS(false);
      getChips('KPI').then((currentChips) => {
        const currentTextOnly = Util.textOnly(currentChips);
        KPIChips.forEach((value) => {
          if (!currentTextOnly.includes(value) && value !== '' && value.length > 1) {
            patchChips(value, 'KPI', setKPIChips);
          }
        });
      });
    }
  }, [KPIChips, ShouldUpdateKPIs]);

  // Handle Location updates
  useEffect(() => {
    if (ShouldUpdateLocations) {
      setShouldUpdateLocations(false);
      getChips('Location').then((currentChips) => {
        const currentTextOnly = Util.textOnly(currentChips);
        LocationChips.forEach((value) => {
          if (!currentTextOnly.includes(value) && value !== '' && value.length > 1) {
            patchChips(value, 'Location', setLocationChips);
          }
        });
      });
    }
  }, [LocationChips, ShouldUpdateLocations]);

  const patchChips = async (text, value_type, setter) => {
    const requestData = {
      queryParams: {
        organization: effectiveOrg,
        value_type: value_type,
        value: text,
      },
    };
    try {
      await Util.post('Resty', '/items/organization_configuration', requestData);
      getChips(value_type).then((e) => {
        setter(() => Util.textOnly(e).sort());
      });
    } catch (error) {
      Util.printAPIError(error);
    }
  };

  const getChips = useCallback(async (value_type) => {
    const requestData = {
      queryParams: {
        organization: effectiveOrg,
        value_type: value_type,
      },
    };
    try {
      const data = await Util.get(
        'Resty',
        '/items/organization_configuration/value_type',
        requestData,
      );
      return data.items || [];
    } catch (error) {
      Util.printAPIError(error);
    }
    return [];
  }, [effectiveOrg]);

  const patchDelete = async (item) => {
    const requestData = {
      queryParams: {
        organization: effectiveOrg,
        timestamp: item.timestamp,
      },
    };
    try {
      await Util.del('Resty', '/items/organization_configuration', requestData);
    } catch (error) {
      Util.printAPIError(error);
    }
  };

  const deleteChips = async (deletedList, value_type) => {
    let deleted = 0;
    const chips = await getChips(value_type);
    const to = Util.textOnly(chips);
    for (const value of deletedList) {
      if (deleted < maxDelete && to.includes(value)) {
        deleted += 1;
        const fullitem = chips.find((e) => e.value === value);
        await patchDelete(fullitem);
      }
    }
  };

  const deleteKPI = async (deletedList) => {
    await deleteChips(deletedList, 'KPI');
    const chips = await getChips('KPI');
    setKPIChips(Util.textOnly(chips).sort());
  };

  const deleteLocation = async (deletedList) => {
    await deleteChips(deletedList, 'Location');
    const chips = await getChips('Location');
    setLocationChips(Util.textOnly(chips).sort());
  };

  const addKPI = () => setShouldUpdateKPIS(true);
  const addLocation = () => setShouldUpdateLocations(true);

  const kpiMenuActions = [
    {
      label: t('common:actions.delete'),
      destructive: true,
      onPress: (element) => deleteKPI([element]),
    },
  ];

  const locationMenuActions = [
    {
      label: t('tags.generateQrCode'),
      onPress: (element) => setQRModalLocation(element),
    },
    {
      label: t('common:actions.delete'),
      destructive: true,
      onPress: (element) => deleteLocation([element]),
    },
  ];

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
          <CustomText style={styles.loadingText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('tags.loading')}
          </CustomText>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Hazard Classifications section */}
      <CustomText style={styles.heading} font="semiBold" size={18} color={AppStyles.colorSet.textPrimary}>
        {t('tags.hazardTitle')}
      </CustomText>
      <CustomText style={styles.explanationText} font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
        {t('tags.hazardDescription')}
      </CustomText>
      <ChipSection
        styles={isDesktop ? Web_Chip_Styles : Chip_Styles}
        deleteChips={deleteKPI}
        addChip={addKPI}
        doSynchronize={true}
        canAdd={true}
        canDelete={true}
        Chips={KPIChips}
        sortChips={true}
        setChips={setKPIChips}
        maxSelect={maxDelete}
        chipMenuActions={kpiMenuActions}
      />

      {/* Locations section */}
      <View style={styles.separator} />
      <CustomText style={styles.heading} font="semiBold" size={18} color={AppStyles.colorSet.textPrimary}>
        {t('tags.locationsTitle')}
      </CustomText>
      <CustomText style={styles.explanationText} font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
        {t('tags.locationsDescription')}
      </CustomText>
      <ChipSection
        styles={isDesktop ? Web_Chip_Styles : Chip_Styles}
        deleteChips={deleteLocation}
        addChip={addLocation}
        doSynchronize={true}
        canAdd={true}
        canDelete={true}
        Chips={LocationChips}
        sortChips={true}
        setChips={setLocationChips}
        maxSelect={maxDelete}
        chipMenuActions={locationMenuActions}
      />

      <QRCodeModal
        visible={QRModalLocation != null}
        organization={effectiveOrg}
        location={QRModalLocation}
        onClose={() => setQRModalLocation(null)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 12,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: hp(3),
  },
  loadingText: {
    marginLeft: 10,
  },
  heading: {
    marginTop: hp(1),
    marginBottom: hp(0.5),
  },
  explanationText: {
    marginBottom: hp(1.5),
    lineHeight: 20,
  },
  separator: {
    height: 1,
    backgroundColor: '#E5E5E5',
    marginVertical: hp(2),
  },
});

export default TagManagementSection;
